/* Audiocolour Online Metronome interface. Requires core, sounds and audio modules. */
(function (root) {
  "use strict";
  if (root.AudiocolourMetronome) return;

  var instances = typeof WeakMap === "function" ? new WeakMap() : null;
  var accentsCycle = ["accent", "normal", "soft", "mute"];
  var STOP_MESSAGES = {
    user: "Metronome stopped.",
    page_hidden: "Playback stopped because this page is hidden. Select Start when you return.",
    context_interrupted: "Audio playback was interrupted. Check your audio output, then select Start to resume.",
    audio_error: "Audio playback stopped after an error. Check your audio output, then select Start to try again.",
    timer_failed: "Playback stopped because its audio scheduler failed. Select Start to try again.",
    audio_schedule_failed: "Playback stopped because a click could not be scheduled. Select Start to try again.",
    context_error: "Playback stopped because the audio device was interrupted. Check the device, then select Start to try again.",
    cancelled: "Playback startup was cancelled. Select Start to try again."
  };
  var controls = {
    bpm: "[data-metronome-bpm]", tempo: "[data-metronome-tempo]", numerator: "[data-metronome-numerator]",
    denominator: "[data-metronome-denominator]", subdivision: "[data-metronome-subdivision]", sound: "[data-metronome-sound]",
    volume: "[data-metronome-volume]", toggle: "[data-metronome-toggle]", beats: "[data-metronome-beats]",
    status: "[data-metronome-status]", beatStatus: "[data-metronome-beat-status]",
    grouping: "[data-metronome-grouping]", swing: "[data-metronome-swing]", countIn: "[data-metronome-count-in]",
    resetAccents: "[data-metronome-reset-accents]", exportMode: "[data-metronome-export-mode]",
    exportLength: "[data-metronome-export-length]", exportLengthHint: "[data-metronome-export-length-hint]",
    exportRate: "[data-metronome-export-rate]", exportRender: "[data-metronome-export-render]",
    exportCancel: "[data-metronome-export-cancel]", exportPanel: "[data-metronome-export-panel]",
    exportStatus: "[data-metronome-export-status]", exportProgress: "[data-metronome-export-progress]",
    exportResult: "[data-metronome-export-result]", exportSummary: "[data-metronome-export-summary]",
    exportAudio: "[data-metronome-export-audio]", exportDownload: "[data-metronome-export-download]"
  };
  var unavailable = "Metronome playback is unavailable because a required module could not be loaded.";

  function listen(instance, element, name, handler) {
    if (!element || !element.addEventListener) return;
    element.addEventListener(name, handler);
    instance.listeners.push([element, name, handler]);
  }
  function num(element) { return Number(element.value); }
  function parseGrouping(value) {
    if (typeof value !== "string" || !/^\s*\d+(?:\s*\+\s*\d+)*\s*$/.test(value)) return null;
    return value.split("+").map(function (part) { return Number(part.trim()); });
  }
  function swingDescription(percent) { return percent === 66.7 ? "66.7% triplet shuffle" : String(percent) + (percent === 50 ? "% straight" : "% swing"); }
  function clearStaleBpmGuard(instance, value) {
    if (instance.bpmCommitGuard !== null && value !== instance.bpmCommitGuard) instance.bpmCommitGuard = null;
  }
  function setMessage(instance, message, isError) {
    instance.status.textContent = message;
    instance.status.classList.toggle("ac-metronome__notice--error", !!isError);
    instance.status.setAttribute("role", isError ? "alert" : "status");
  }
  function valid(instance, candidate, field) {
    var input = instance[field];
    var result = instance.core.validateSettings(candidate);
    if (input && input.validity && input.validity.valid === false) result = { ok: false, error: { message: "Enter a value within the displayed range and step." } };
    if (!result.ok) {
      if (input) input.setAttribute("aria-invalid", "true");
      cancelRestart(instance);
      setMessage(instance, result.error.message || "Check the metronome settings.", true);
      return null;
    }
    ["bpm", "tempo", "numerator", "denominator", "subdivision", "grouping", "swing", "countIn"].forEach(function (key) {
      if (instance[key]) instance[key].removeAttribute("aria-invalid");
    });
    return result.settings;
  }
  function valuesFromControls(instance) {
    var grouping = instance.grouping ? parseGrouping(instance.grouping.value) : instance.state.grouping;
    return {
      bpm: num(instance.bpm), numerator: num(instance.numerator), denominator: num(instance.denominator),
      subdivision: num(instance.subdivision), swingPercent: instance.swing ? num(instance.swing) : 50,
      countInBars: instance.countIn ? num(instance.countIn) : 0,
      grouping: grouping || [], accents: instance.state.accents
    };
  }
  function syncValues(instance, preserveBpmText) {
    if (!preserveBpmText) instance.bpm.value = String(instance.state.settings.bpm);
    instance.tempo.value = String(instance.state.settings.bpm);
    instance.numerator.value = String(instance.state.settings.numerator);
    instance.denominator.value = String(instance.state.settings.denominator);
    instance.subdivision.value = String(instance.state.settings.subdivision);
    instance.sound.value = instance.state.sound;
    instance.volume.value = String(instance.state.volume);
    if (instance.grouping) instance.grouping.value = instance.state.grouping.join("+");
    if (instance.swing) instance.swing.value = String(instance.state.settings.swingPercent);
    if (instance.countIn) instance.countIn.value = String(instance.state.settings.countInBars);
    var output = instance.root.querySelector(".ac-metronome__value[for=\"" + instance.volume.id + "\"]");
    if (output) output.textContent = instance.state.volume + "%";
    var swingOutput = instance.root.querySelector(".ac-metronome__value[for=\"" + instance.swing.id + "\"]");
    if (swingOutput) swingOutput.textContent = swingDescription(instance.state.settings.swingPercent);
  }
  function defaultLayout(instance, numerator, denominator) {
    return instance.core.beatLayout({ numerator: numerator, denominator: denominator });
  }
  function renderBeats(instance) {
    var layout = instance.core.beatLayout({ numerator: instance.state.settings.numerator, denominator: instance.state.settings.denominator, grouping: instance.state.grouping, accents: instance.state.accents });
    if (!layout.ok) return;
    instance.state.layout = layout;
    while (instance.beats.firstChild) instance.beats.removeChild(instance.beats.firstChild);
    layout.beats.forEach(function (beat) {
      var item = root.document.createElement("li");
      var button = root.document.createElement("button");
      var label = ["accent", "normal", "soft", "mute"][accentsCycle.indexOf(beat.accentLevel)];
      item.className = "ac-metronome__beat-cell" + (beat.isGroupStart && beat.beatIndex > 0 ? " ac-metronome__beat-cell--group-start" : "");
      button.type = "button";
      button.className = "ac-metronome__beat" + (beat.accentLevel === "accent" ? " ac-metronome__beat--accent" : "") + (beat.accentLevel === "mute" ? " ac-metronome__beat--muted" : "");
      button.setAttribute("data-beat-index", String(beat.beatIndex));
      button.setAttribute("aria-label", "Beat " + (beat.beatIndex + 1) + ", " + label + ". Activate to change accent.");
      button.textContent = String(beat.beatIndex + 1) + (beat.isGroupStart && beat.beatIndex > 0 ? " ·" : "");
      item.appendChild(button);
      instance.beats.appendChild(item);
    });
    instance.beats.setAttribute("aria-label", layout.numerator + " beat accents");
  }
  function updateBeatButton(instance, beatIndex) {
    var layout = instance.core.beatLayout({ numerator: instance.state.settings.numerator, denominator: instance.state.settings.denominator, grouping: instance.state.grouping, accents: instance.state.accents });
    if (!layout.ok || !layout.beats[beatIndex]) return;
    instance.state.layout = layout;
    var beat = layout.beats[beatIndex];
    var button = instance.beats.querySelector('[data-beat-index="' + beatIndex + '"]');
    if (!button) return;
    var label = ["accent", "normal", "soft", "mute"][accentsCycle.indexOf(beat.accentLevel)];
    button.className = "ac-metronome__beat" + (beat.accentLevel === "accent" ? " ac-metronome__beat--accent" : "") + (beat.accentLevel === "mute" ? " ac-metronome__beat--muted" : "");
    button.setAttribute("aria-label", "Beat " + (beat.beatIndex + 1) + ", " + label + ". Activate to change accent.");
  }
  function settingsNow(instance) {
    var settings = valuesFromControls(instance);
    var checked = instance.core.validateSettings(settings);
    if (!checked.ok) return null;
    return {
      bpm: checked.settings.bpm,
      numerator: checked.settings.numerator,
      denominator: checked.settings.denominator,
      grouping: checked.settings.grouping.slice(),
      accents: instance.state.accents.slice(),
      subdivision: checked.settings.subdivision,
      swingPercent: checked.settings.swingPercent,
      countInBars: checked.settings.countInBars
    };
  }
  function cancelRestart(instance) {
    if (instance.restartTimer !== null) root.clearTimeout(instance.restartTimer);
    instance.restartTimer = null;
    instance.queuedRestart = false;
    instance.pendingBpmCommitValue = null;
  }
  function stopVisual(instance) {
    if (instance.raf !== null && root.cancelAnimationFrame) root.cancelAnimationFrame(instance.raf);
    instance.raf = null;
    instance.visualEvents = [];
    var active = instance.beats.querySelector(".ac-metronome__beat--current");
    if (active) active.classList.remove("ac-metronome__beat--current");
  }
  function currentBeat(instance, event) {
    var current = instance.beats.querySelector(".ac-metronome__beat--current");
    if (current) current.classList.remove("ac-metronome__beat--current");
    var button = instance.beats.querySelector('[data-beat-index="' + event.beatIndex + '"]');
    if (button) button.classList.add("ac-metronome__beat--current");
  }
  function readEngineState(instance) {
    try { instance.engineState = instance.engine.getState(); }
    catch (_) { instance.engineState = null; }
    return instance.engineState;
  }
  function paint(instance) {
    if (instance.disposed) return;
    var now = root.performance && root.performance.now ? root.performance.now() : Date.now();
    while (instance.visualEvents.length && instance.visualEvents[0].at <= now) currentBeat(instance, instance.visualEvents.shift().event);
    if (instance.playing && root.requestAnimationFrame) instance.raf = root.requestAnimationFrame(function () { paint(instance); });
  }
  function onEngineEvent(instance, event) {
    if (event.type === "scheduled" && event.subdivisionIndex === 0) {
      if (!root.requestAnimationFrame) return;
      var now = root.performance && root.performance.now ? root.performance.now() : Date.now();
      var snapshot = readEngineState(instance);
      var outputLatency = snapshot && typeof snapshot.outputLatency === "number" && isFinite(snapshot.outputLatency) && snapshot.outputLatency > 0 ? snapshot.outputLatency : 0;
      instance.visualEvents.push({ at: now + Math.max(0, event.scheduledTime - event.observedTime + outputLatency) * 1000, event: event });
      instance.visualEvents.sort(function (a, b) { return a.at - b.at; });
      if (instance.raf === null && root.requestAnimationFrame) paint(instance);
    } else if (event.type === "stopped" || event.type === "destroyed") {
      if (instance.operation && event.type === "stopped") return;
      readEngineState(instance);
      instance.playing = false;
      instance.lastStopReason = event.reason || (event.type === "destroyed" ? "destroyed" : "unknown");
      stopVisual(instance);
      if (!instance.disposed) {
        instance.toggle.textContent = "Start";
        if (event.type === "destroyed") setMessage(instance, "The audio engine was closed. Reload the page to use the metronome again.", true);
        else setMessage(instance, STOP_MESSAGES[instance.lastStopReason] || "Playback stopped. Check your audio output, then select Start to try again.", instance.lastStopReason !== "user");
      }
    } else if (event.type === "error") {
      instance.playing = false;
      instance.operation = false;
      instance.queuedRestart = false;
      instance.toggle.textContent = "Start";
      cancelRestart(instance);
      instance.lastStopReason = event.error && event.error.code || "audio_error";
      stopVisual(instance);
      setMessage(instance, (event.error && event.error.message ? event.error.message + " " : "Audio playback stopped after an error. ") + "Check your audio output, then select Start to try again.", true);
    }
  }
  function beginStart(instance, restart) {
    if (instance.disposed || instance.operation) { if (restart) instance.queuedRestart = true; return; }
    var settings = settingsNow(instance);
    if (!settings) { setMessage(instance, "Playback settings are invalid. Correct the highlighted value before restarting.", true); return; }
    instance.operation = true;
    var token = ++instance.operationToken;
    instance.playing = false;
    instance.toggle.textContent = "Starting…";
    setMessage(instance, restart ? "Applying the latest rhythm settings…" : "Starting metronome…");
    var result;
    try { result = instance.engine.start(settings); }
    catch (error) { result = Promise.resolve({ ok: false, error: { message: error.message } }); }
    Promise.resolve(result).then(function (response) {
      if (token !== instance.operationToken) return;
      instance.operation = false;
      if (instance.disposed) return;
      var engineState = readEngineState(instance);
      if (!response || !response.ok || (engineState && engineState.status !== "playing")) {
        instance.playing = false;
        instance.queuedRestart = false;
        instance.toggle.textContent = "Start";
        setMessage(instance, response && response.error && response.error.message || "Metronome could not start. Check audio permissions and try again.", true);
      } else {
        instance.playing = true;
        instance.lastStopReason = null;
        instance.toggle.textContent = "Stop";
        setMessage(instance, "Metronome playing.");
        if (instance.raf === null && root.requestAnimationFrame) paint(instance);
      }
      if (instance.queuedRestart && instance.playing) {
        instance.queuedRestart = false;
        restartNow(instance);
      }
    }, function (error) {
      if (token !== instance.operationToken) return;
      instance.operation = false;
      instance.playing = false;
      if (!instance.disposed) { instance.toggle.textContent = "Start"; setMessage(instance, error && error.message || "Metronome could not start.", true); }
    });
  }
  function restartNow(instance) {
    if (!instance.playing) { if (instance.operation) instance.queuedRestart = true; return; }
    if (instance.operation) { instance.queuedRestart = true; return; }
    instance.operation = true;
    var token = ++instance.operationToken;
    instance.playing = false;
    instance.toggle.textContent = "Starting…";
    stopVisual(instance);
    try { instance.engine.stop(); } catch (_) {}
    var settings = settingsNow(instance);
    if (!settings) { instance.operation = false; instance.toggle.textContent = "Start"; setMessage(instance, "Playback settings are invalid. Correct the highlighted value before restarting.", true); return; }
    var result;
    try { result = instance.engine.start(settings); }
    catch (error) { result = Promise.resolve({ ok: false, error: { message: error.message } }); }
    Promise.resolve(result).then(function (response) {
      if (token !== instance.operationToken) return;
      instance.operation = false;
      if (instance.disposed) return;
      var engineState = readEngineState(instance);
      if (!response || !response.ok || (engineState && engineState.status !== "playing")) {
        instance.queuedRestart = false;
        instance.toggle.textContent = "Start";
        setMessage(instance, response && response.error && response.error.message || "The updated rhythm could not start.", true);
      } else {
        instance.playing = true;
        instance.lastStopReason = null;
        instance.toggle.textContent = "Stop";
        setMessage(instance, "Metronome playing.");
        if (instance.raf === null && root.requestAnimationFrame) paint(instance);
      }
      if (instance.queuedRestart && instance.playing) { instance.queuedRestart = false; restartNow(instance); }
    }, function (error) {
      if (token !== instance.operationToken) return;
      instance.operation = false; instance.queuedRestart = false; instance.toggle.textContent = "Start";
      if (!instance.disposed) setMessage(instance, error && error.message || "The updated rhythm could not start.", true);
    });
  }
  function scheduleRestart(instance, delay) {
    if (!instance.playing && !instance.operation) return;
    if (instance.restartTimer !== null) root.clearTimeout(instance.restartTimer);
    if (delay <= 0) {
      instance.restartTimer = null;
      instance.pendingBpmCommitValue = null;
      if (instance.operation) instance.queuedRestart = true;
      else restartNow(instance);
      return;
    }
    instance.restartTimer = root.setTimeout(function () {
      instance.restartTimer = null;
      if (instance.pendingBpmCommitValue !== null && num(instance.bpm) === instance.pendingBpmCommitValue) {
        instance.bpmCommitGuard = instance.pendingBpmCommitValue;
        instance.pendingBpmCommitValue = null;
      }
      if (instance.operation) instance.queuedRestart = true;
      else restartNow(instance);
    }, delay);
  }
  function acceptGrouping(instance) {
    var candidate = valuesFromControls(instance);
    var checked = valid(instance, candidate, "grouping");
    if (!checked) return false;
    var previous = instance.core.beatLayout({
      numerator: checked.numerator, denominator: checked.denominator, grouping: instance.state.grouping
    });
    var next = instance.core.beatLayout({
      numerator: checked.numerator, denominator: checked.denominator, grouping: checked.grouping
    });
    if (previous.ok && next.ok) {
      var accents = instance.state.accents.slice();
      for (var i = 0; i < accents.length; i += 1) {
        if (accents[i] === previous.beats[i].accentLevel) accents[i] = next.beats[i].accentLevel;
      }
      instance.state.accents = accents;
    }
    instance.state.settings = instance.core.validateSettings(valuesFromControls(instance)).settings;
    instance.state.grouping = checked.grouping.slice();
    syncValues(instance);
    renderBeats(instance);
    setMessage(instance, "Grouping updated. Default accents follow the new groups; custom accents are preserved.");
    scheduleRestart(instance, 150);
    return true;
  }
  function acceptSettings(instance, delay, field, changedBeatIndex) {
    var candidate = valuesFromControls(instance);
    var checked = valid(instance, candidate, field || "bpm");
    if (!checked) return false;
    instance.state.settings = checked;
    instance.state.grouping = checked.grouping.slice();
    if (field === "grouping") instance.grouping.removeAttribute("aria-invalid");
    if (field === "bpm" && delay === 400) {
      instance.pendingBpmCommitValue = num(instance.bpm);
      if (!instance.playing && !instance.operation) {
        instance.bpmCommitGuard = instance.pendingBpmCommitValue;
        instance.pendingBpmCommitValue = null;
      }
    }
    syncValues(instance, field === "bpm" && delay === 400);
    if (field === "accent") updateBeatButton(instance, changedBeatIndex);
    else if (field !== "bpm") renderBeats(instance);
    setMessage(instance, field === "accents-reset" ? "Accents reset to the current grouping defaults." : "Settings ready.");
    scheduleRestart(instance, delay);
    return true;
  }
  function stop(instance, destroy, message) {
    cancelRestart(instance);
    instance.operationToken += 1;
    instance.playing = false;
    instance.operation = false;
    instance.toggle.textContent = "Start";
    stopVisual(instance);
    var stopResult = null;
    if (instance.engine) {
      try { if (destroy) instance.engine.destroy(); else stopResult = instance.engine.stop(); } catch (_) {}
    }
    if (!destroy && message) setMessage(instance, message);
    else if (!destroy && !(stopResult && stopResult.alreadyStopped && instance.lastStopReason && instance.lastStopReason !== "user")) setMessage(instance, "Metronome stopped.");
  }
  function suspendForBfcache(instance) {
    if (instance.disposed) return;
    instance.bfcacheSuspended = true;
    if (instance.activeExportId) cancelExport(instance, "WAV rendering was cancelled because this page was saved. Select Render WAV to try again.", false);
    stop(instance, false, "Playback stopped while this page was saved. Select Start to resume.");
  }
  function restoreFromBfcache(instance) {
    if (instance.disposed || !instance.bfcacheSuspended) return;
    cancelRestart(instance);
    instance.operationToken += 1;
    instance.playing = false;
    instance.operation = false;
    instance.queuedRestart = false;
    instance.toggle.textContent = "Start";
    stopVisual(instance);
    readEngineState(instance);
    instance.lastStopReason = "page_hidden";
    instance.bfcacheSuspended = false;
    setMessage(instance, "Metronome ready. Start playback when you’re ready.");
  }
  function setExportMessage(instance, message, isError) {
    instance.exportStatus.textContent = message;
    instance.exportStatus.setAttribute("role", isError ? "alert" : "status");
    instance.exportPanel.setAttribute("aria-busy", instance.activeExportId ? "true" : "false");
  }
  function clearExportResult(instance) {
    try { if (instance.exportAudio.pause) instance.exportAudio.pause(); } catch (_) {}
    instance.exportAudio.removeAttribute("src");
    instance.exportAudio.hidden = true;
    if (instance.exportAudio.load) { try { instance.exportAudio.load(); } catch (_) {} }
    instance.exportDownload.removeAttribute("href");
    instance.exportDownload.removeAttribute("download");
    instance.exportDownload.hidden = true;
    instance.exportResult.hidden = true;
    instance.exportSummary.textContent = "";
    if (instance.exportObjectUrl && root.URL && root.URL.revokeObjectURL) {
      try { root.URL.revokeObjectURL(instance.exportObjectUrl); } catch (_) {}
    }
    instance.exportObjectUrl = null;
  }
  function setExportBusy(instance, busy) {
    instance.exportPanel.setAttribute("aria-busy", busy ? "true" : "false");
    instance.exportRender.disabled = !!busy;
    instance.exportCancel.disabled = !busy;
    instance.exportCancel.hidden = !busy;
    instance.exportProgress.hidden = !busy;
    if (busy) instance.exportProgress.removeAttribute("value");
  }
  function terminateExportWorker(instance) {
    if (instance.exportWorker) {
      try { instance.exportWorker.terminate(); } catch (_) {}
    }
    instance.exportWorker = null;
  }
  function cancelExport(instance, message, clearResult) {
    var wasActive = !!instance.activeExportId;
    instance.activeExportId = 0;
    instance.exportSequence += 1;
    if (wasActive) terminateExportWorker(instance);
    setExportBusy(instance, false);
    if (clearResult) clearExportResult(instance);
    if (message) setExportMessage(instance, message, false);
  }
  function formatExportDuration(seconds) {
    if (seconds < 60) return (Math.round(seconds * 10) / 10).toFixed(1).replace(/\.0$/, "") + " sec";
    var minutes = (seconds / 60).toFixed(1).replace(/\.0$/, "");
    return minutes + " min";
  }
  function updateExportLengthMode(instance) {
    if (instance.exportMode.value === "duration") {
      instance.exportLength.setAttribute("max", "10");
      instance.exportLengthHint.textContent = "Enter a target duration in whole minutes (1–10). The renderer converts it to whole bars, including the selected count-in; the actual duration can differ slightly.";
    } else {
      instance.exportLength.removeAttribute("max");
      instance.exportLengthHint.textContent = "Enter a whole number of bars. Count-in bars are added separately. WAV exports are limited to 10 minutes total.";
    }
    instance.exportLength.removeAttribute("aria-invalid");
  }
  function exportSettings(instance) {
    var rawSettings = valuesFromControls(instance);
    var checked = instance.core.validateSettings(rawSettings);
    if (!checked.ok) {
      var errorCode = checked.error && checked.error.code;
      var field = errorCode === "invalid_grouping" || errorCode === "grouping_sum_mismatch" ? instance.grouping
        : errorCode === "invalid_swing" || errorCode === "swing_out_of_range" || errorCode === "swing_precision" ? instance.swing
        : errorCode === "invalid_count_in" ? instance.countIn
        : errorCode === "invalid_subdivision" ? instance.subdivision
        : errorCode === "invalid_bpm" || errorCode === "bpm_out_of_range" ? instance.bpm : null;
      if (field) field.setAttribute("aria-invalid", "true");
      setExportMessage(instance, checked.error.message || "Correct the rhythm settings before exporting.", true);
      return null;
    }
    [instance.bpm, instance.numerator, instance.denominator, instance.subdivision, instance.grouping, instance.swing, instance.countIn].forEach(function (field) {
      field.removeAttribute("aria-invalid");
    });
    var mode = instance.exportMode.value;
    var length = Number(instance.exportLength.value);
    if ((mode !== "bars" && mode !== "duration") || !Number.isSafeInteger(length) || length < 1 ||
      (mode === "duration" && length > 10) || (instance.exportLength.validity && !instance.exportLength.validity.valid)) {
      instance.exportLength.setAttribute("aria-invalid", "true");
      setExportMessage(instance, mode === "duration"
        ? "Enter a whole target duration from 1 to 10 minutes."
        : "Enter a whole number of bars greater than zero.", true);
      return null;
    }
    var sampleRate = Number(instance.exportRate.value);
    if (sampleRate !== 44100 && sampleRate !== 48000) {
      instance.exportRate.setAttribute("aria-invalid", "true");
      setExportMessage(instance, "Choose a sample rate of 44.1 or 48 kHz.", true);
      return null;
    }
    instance.exportRate.removeAttribute("aria-invalid");
    var bars = length;
    if (mode === "duration") {
      var bar = instance.core.barDuration({ bpm: checked.settings.bpm, numerator: checked.settings.numerator, denominator: checked.settings.denominator });
      if (!bar.ok || !bar.seconds) {
        setExportMessage(instance, "The selected tempo and meter cannot be used to calculate the export length.", true);
        return null;
      }
      var countInBars = checked.settings.countInBars;
      var totalBars = Math.round(length * 60 / bar.seconds);
      bars = Math.max(1, totalBars - countInBars);
      var wavLimits = root.AudiocolourMetronomeWav && root.AudiocolourMetronomeWav.limits;
      var maxDurationSeconds = wavLimits && typeof wavLimits.maxDurationSeconds === "number" &&
        isFinite(wavLimits.maxDurationSeconds) && wavLimits.maxDurationSeconds > 0
        ? wavLimits.maxDurationSeconds : 600;
      var durationWithinLimit = function () {
        return (countInBars + bars) * bar.seconds <= maxDurationSeconds;
      };
      if (!durationWithinLimit()) {
        totalBars = Math.floor(maxDurationSeconds / bar.seconds);
        bars = Math.max(1, totalBars - countInBars);
        while (!durationWithinLimit()) {
          if (bars <= 1) {
            setExportMessage(instance, "The selected tempo and meter cannot be used to calculate the export length.", true);
            return null;
          }
          totalBars -= 1;
          bars = Math.max(1, totalBars - countInBars);
        }
      }
    }
    return {
      settings: {
        bpm: checked.settings.bpm,
        numerator: checked.settings.numerator,
        denominator: checked.settings.denominator,
        grouping: checked.settings.grouping.slice(),
        accents: instance.state.accents.slice(),
        subdivision: checked.settings.subdivision,
        swingPercent: checked.settings.swingPercent,
        countInBars: checked.settings.countInBars
      },
      bars: bars,
      sound: instance.sound.value,
      sampleRate: sampleRate
    };
  }
  function finishExportError(instance, id, message) {
    if (!id || instance.activeExportId !== id) return;
    instance.activeExportId = 0;
    terminateExportWorker(instance);
    setExportBusy(instance, false);
    instance.exportProgress.hidden = true;
    setExportMessage(instance, message || "The WAV could not be rendered. Check the export settings and try again.", true);
  }
  function handleExportWorkerMessage(instance, event) {
    var message = event && event.data;
    if (!message || message.id !== instance.activeExportId) return;
    if (message.type === "progress") {
      instance.exportProgress.hidden = false;
      instance.exportProgress.removeAttribute("value");
      setExportMessage(instance, message.message || "Rendering click track…", false);
      return;
    }
    if (message.type === "error") {
      finishExportError(instance, message.id, message.error && message.error.message);
      return;
    }
    if (message.type !== "complete") return;
    var id = message.id;
    try {
      var blob = new root.Blob([message.bytes], { type: message.mimeType || "audio/wav" });
      if (!root.URL || !root.URL.createObjectURL) throw new Error("This browser cannot create a temporary WAV download.");
      instance.exportObjectUrl = root.URL.createObjectURL(blob);
      instance.exportAudio.setAttribute("src", instance.exportObjectUrl);
      instance.exportAudio.hidden = false;
      if (instance.exportAudio.load) instance.exportAudio.load();
      instance.exportDownload.setAttribute("href", instance.exportObjectUrl);
      instance.exportDownload.setAttribute("download", message.suggestedFilename || "audiocolour-click-track.wav");
      instance.exportDownload.hidden = false;
      var metadata = message.metadata || {};
      var countInBars = metadata.countInBars || 0;
      instance.exportSummary.textContent = (metadata.bars || 0) + " bars + " + countInBars + " count-in " + (countInBars === 1 ? "bar" : "bars") + " · " +
        metadata.numerator + "/" + metadata.denominator + " · " + metadata.bpm + " BPM · " +
        (metadata.sampleRate / 1000) + " kHz · " + formatExportDuration(metadata.durationSeconds || 0) + ".";
      instance.exportResult.hidden = false;
      instance.activeExportId = 0;
      setExportBusy(instance, false);
      instance.exportProgress.hidden = true;
      setExportMessage(instance, "WAV ready. Preview or download the generated click track.", false);
    } catch (error) {
      if (instance.exportObjectUrl && root.URL && root.URL.revokeObjectURL) root.URL.revokeObjectURL(instance.exportObjectUrl);
      instance.exportObjectUrl = null;
      finishExportError(instance, id, error && error.message);
    }
  }
  function startExport(instance) {
    if (instance.activeExportId) return;
    var options = exportSettings(instance);
    if (!options) return;
    clearExportResult(instance);
    if (typeof root.Worker !== "function") {
      setExportMessage(instance, "Background WAV rendering is not supported in this browser.", true);
      return;
    }
    var id = ++instance.exportSequence;
    instance.activeExportId = id;
    var worker;
    try {
      var workerUrl = instance.root.getAttribute("data-metronome-worker-url");
      if (!workerUrl) throw new Error("The WAV worker asset URL is missing.");
      worker = instance.exportWorker || new root.Worker(workerUrl);
      instance.exportWorker = worker;
      worker.onmessage = function (event) { handleExportWorkerMessage(instance, event); };
      worker.onerror = function (event) {
        if (event && event.preventDefault) event.preventDefault();
        finishExportError(instance, id, "The background WAV renderer could not load. Refresh the page and try again.");
      };
    } catch (error) {
      instance.activeExportId = 0;
      setExportMessage(instance, error && error.message === "The WAV worker asset URL is missing."
        ? error.message
        : "The background WAV renderer could not start. Check browser worker permissions and try again.", true);
      return;
    }
    setExportBusy(instance, true);
    setExportMessage(instance, "Starting the background WAV renderer…", false);
    try {
      worker.postMessage({
        type: "render", id: id, options: options,
        assets: {
          core: instance.root.getAttribute("data-metronome-core-url"),
          sounds: instance.root.getAttribute("data-metronome-sounds-url"),
          wav: instance.root.getAttribute("data-metronome-wav-url")
        }
      });
    } catch (_) {
      finishExportError(instance, id, "The export settings could not be sent to the background renderer.");
    }
  }
  function createInstance(host, api) {
    var instance = { root: host, core: api.core, listeners: [], restartTimer: null, raf: null, visualEvents: [], operation: false, operationToken: 0, queuedRestart: false, playing: false, disposed: false };
    instance.exportSequence = 0;
    instance.activeExportId = 0;
    instance.exportWorker = null;
    instance.exportObjectUrl = null;
    Object.keys(controls).forEach(function (key) { instance[key] = host.querySelector(controls[key]); });
    if (Object.keys(controls).some(function (key) { return !instance[key]; })) return null;
    instance.state = { sound: "classic", volume: 80, settings: null, grouping: null, accents: null, layout: null };
    var initial = api.core.validateSettings({ bpm: 120, numerator: 4, denominator: 4, subdivision: 1, countInBars: 0 });
    if (!initial.ok) return null;
    instance.state.settings = initial.settings;
    instance.state.grouping = initial.settings.grouping.slice();
    instance.state.accents = initial.settings.beats.map(function (beat) { return beat.accentLevel; });
    updateExportLengthMode(instance);
    instance.sound.innerHTML = "";
    api.sounds.soundNames.forEach(function (name) {
      var option = root.document.createElement("option"); option.value = name; option.textContent = name === "hihat" ? "Hi-hat" : name.charAt(0).toUpperCase() + name.slice(1); instance.sound.appendChild(option);
    });
    var engineResult = api.audio.createEngine({ sound: instance.state.sound, volume: instance.state.volume, onEvent: function (event) { onEngineEvent(instance, event); } });
    if (!engineResult.ok) { setMessage(instance, engineResult.error && engineResult.error.message || unavailable, true); return null; }
    instance.engine = engineResult.engine;
    readEngineState(instance);
    syncValues(instance); renderBeats(instance);
    host.querySelectorAll("[data-metronome-active]").forEach(function (element) { element.disabled = false; });
    instance.toggle.textContent = "Start";
    setMessage(instance, "Metronome ready. Start playback when you’re ready.");
    listen(instance, instance.toggle, "click", function () {
      if (instance.playing || instance.operation) stop(instance, false);
      else beginStart(instance, false);
    });
    listen(instance, instance.bpm, "input", function () {
      clearStaleBpmGuard(instance, num(instance.bpm));
      acceptSettings(instance, 400, "bpm");
    });
    instance.bpmCommitGuard = null;
    instance.pendingBpmCommitValue = null;
    listen(instance, instance.bpm, "change", function () {
      if (instance.bpmCommitGuard !== null && num(instance.bpm) === instance.bpmCommitGuard) return;
      acceptSettings(instance, 400, "bpm");
    });
    listen(instance, instance.bpm, "keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (instance.bpmCommitGuard === null || num(instance.bpm) !== instance.bpmCommitGuard) {
          if (acceptSettings(instance, 0, "bpm")) instance.bpmCommitGuard = num(instance.bpm);
        }
        instance.bpm.blur();
      }
      else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        var next = num(instance.bpm) + (event.key === "ArrowUp" ? 0.1 : -0.1);
        instance.bpm.value = String(Math.max(20, Math.min(300, Math.round(next * 10) / 10)));
        clearStaleBpmGuard(instance, num(instance.bpm));
        if (acceptSettings(instance, 200, "bpm")) instance.bpmCommitGuard = num(instance.bpm);
      }
    });
    listen(instance, instance.bpm, "blur", function () {
      if (instance.bpmCommitGuard !== null && num(instance.bpm) === instance.bpmCommitGuard) return;
      if (acceptSettings(instance, 0, "bpm")) instance.bpmCommitGuard = num(instance.bpm);
    });
    instance.tempoInteraction = null;
    instance.lastPointerTempoCommit = null;
    listen(instance, instance.tempo, "pointerdown", function () {
      instance.lastPointerTempoCommit = null;
      instance.tempoInteraction = { kind: "pointer", startBpm: num(instance.bpm) };
    });
    listen(instance, instance.tempo, "keydown", function (event) {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "PageDown" || event.key === "PageUp" || event.key === "Home" || event.key === "End") {
        instance.lastPointerTempoCommit = null;
        instance.tempoInteraction = { kind: "keyboard" };
      }
    });
    listen(instance, instance.tempo, "input", function () {
      instance.bpm.value = instance.tempo.value;
      var checked = valid(instance, valuesFromControls(instance), "bpm");
      if (!checked) return;
      clearStaleBpmGuard(instance, checked.bpm);
      instance.state.settings = checked;
      syncValues(instance);
      var trailingPointerInput = instance.lastPointerTempoCommit !== null && num(instance.tempo) === instance.lastPointerTempoCommit;
      if ((!instance.tempoInteraction || instance.tempoInteraction.kind === "keyboard") && !trailingPointerInput) scheduleRestart(instance, 200);
    });
    listen(instance, root, "pointerup", function () {
      var interaction = instance.tempoInteraction;
      if (!interaction || interaction.kind !== "pointer") return;
      instance.tempoInteraction = null;
      instance.lastPointerTempoCommit = num(instance.tempo);
      if (instance.lastPointerTempoCommit !== interaction.startBpm) {
        instance.bpm.value = instance.tempo.value;
        acceptSettings(instance, 0, "bpm");
      }
    });
    listen(instance, root, "pointercancel", function () {
      var interaction = instance.tempoInteraction;
      if (!interaction || interaction.kind !== "pointer") return;
      instance.tempoInteraction = null;
      instance.lastPointerTempoCommit = num(instance.tempo);
      if (instance.lastPointerTempoCommit !== interaction.startBpm) {
        instance.bpm.value = instance.tempo.value;
        acceptSettings(instance, 0, "bpm");
      }
    });
    listen(instance, instance.tempo, "change", function () {
      if (instance.tempoInteraction && instance.tempoInteraction.kind === "pointer") return;
      if (instance.tempoInteraction && instance.tempoInteraction.kind === "keyboard") return;
      if (instance.lastPointerTempoCommit !== null && num(instance.tempo) === instance.lastPointerTempoCommit) {
        instance.lastPointerTempoCommit = null;
        return;
      }
      instance.lastPointerTempoCommit = null;
      instance.bpm.value = instance.tempo.value;
      acceptSettings(instance, 200, "bpm");
    });
    host.querySelectorAll("[data-tempo-step]").forEach(function (button) { listen(instance, button, "click", function () { var next = Math.max(20, Math.min(300, Math.round((num(instance.bpm) + Number(button.getAttribute("data-tempo-step")) * 1) * 10) / 10)); instance.bpm.value = String(next); clearStaleBpmGuard(instance, next); acceptSettings(instance, 200, "bpm"); }); });
    [instance.numerator, instance.denominator, instance.subdivision].forEach(function (field) { listen(instance, field, "change", function () {
      var isMeter = field === instance.numerator || field === instance.denominator;
      if (isMeter) {
        var layout = defaultLayout(instance, num(instance.numerator), num(instance.denominator));
        if (!layout.ok) { valid(instance, valuesFromControls(instance), field === instance.numerator ? "numerator" : "denominator"); return; }
        instance.state.grouping = layout.grouping.slice(); instance.state.accents = layout.beats.map(function (beat) { return beat.accentLevel; });
        instance.grouping.value = instance.state.grouping.join("+");
      }
      acceptSettings(instance, 150, field === instance.numerator ? "numerator" : field === instance.denominator ? "denominator" : "subdivision");
    }); });
    listen(instance, instance.beats, "click", function (event) {
      var button = event.target && event.target.closest ? event.target.closest("[data-beat-index]") : null;
      if (!button || !instance.beats.contains(button)) return;
      var index = Number(button.getAttribute("data-beat-index"));
      var next = accentsCycle[(accentsCycle.indexOf(instance.state.accents[index]) + 1) % accentsCycle.length];
      instance.state.accents[index] = next;
      acceptSettings(instance, 150, "accent", index);
    });
    listen(instance, instance.grouping, "input", function () {
      if (parseGrouping(instance.grouping.value)) instance.grouping.removeAttribute("aria-invalid");
    });
    listen(instance, instance.grouping, "change", function () { acceptGrouping(instance); });
    listen(instance, instance.swing, "change", function () { acceptSettings(instance, 150, "swing"); });
    listen(instance, instance.countIn, "change", function () { acceptSettings(instance, 150, "countIn"); });
    listen(instance, instance.resetAccents, "click", function () {
      var layout = instance.core.beatLayout({ numerator: num(instance.numerator), denominator: num(instance.denominator), grouping: instance.state.grouping });
      if (!layout.ok) { setMessage(instance, layout.error.message, true); return; }
      instance.state.accents = layout.beats.map(function (beat) { return beat.accentLevel; });
      acceptSettings(instance, 150, "accents-reset");
    });
    listen(instance, instance.sound, "change", function () {
      var result = instance.engine.setSound(instance.sound.value);
      if (!result.ok) { setMessage(instance, result.error.message, true); return; }
      readEngineState(instance);
      instance.state.sound = instance.sound.value; setMessage(instance, "Click sound changed.");
    });
    listen(instance, instance.volume, "input", function () {
      var value = Number(instance.volume.value); var result = instance.engine.setVolume(value);
      if (!result.ok) { setMessage(instance, result.error.message, true); return; }
      readEngineState(instance);
      instance.state.volume = value; syncValues(instance);
    });
    listen(instance, instance.exportMode, "change", function () { updateExportLengthMode(instance); });
    listen(instance, instance.exportLength, "input", function () {
      if (Number.isSafeInteger(Number(instance.exportLength.value)) && Number(instance.exportLength.value) > 0 &&
        (instance.exportMode.value !== "duration" || Number(instance.exportLength.value) <= 10)) {
        instance.exportLength.removeAttribute("aria-invalid");
      }
    });
    listen(instance, instance.exportRender, "click", function () { startExport(instance); });
    listen(instance, instance.exportCancel, "click", function () {
      cancelExport(instance, "WAV rendering cancelled.", false);
    });
    instance.cleanup = function (destroy) {
      if (instance.disposed) return;
      instance.disposed = true; stop(instance, destroy);
      cancelExport(instance, null, true);
      terminateExportWorker(instance);
      instance.listeners.forEach(function (entry) { entry[0].removeEventListener(entry[1], entry[2]); });
      instance.listeners = [];
      if (instances) instances.delete(host);
    };
    return instance;
  }
  function init(scope) {
    var core = root.AudiocolourMetronomeCore, sounds = root.AudiocolourMetronomeSounds, audio = root.AudiocolourMetronomeAudio;
    var hosts = scope.querySelectorAll("[data-ac-metronome]");
    Array.prototype.forEach.call(hosts, function (host) {
      if (instances && instances.has(host)) return;
      if (!core || !sounds || !audio) { var status = host.querySelector(controls.status); if (status) { status.textContent = unavailable; status.setAttribute("role", "alert"); } return; }
      if (typeof audio.isSupported === "function" && !audio.isSupported()) {
        var unsupportedStatus = host.querySelector(controls.status);
        if (unsupportedStatus) { unsupportedStatus.textContent = "Audio playback is not supported in this browser."; unsupportedStatus.setAttribute("role", "alert"); }
        return;
      }
      var instance = createInstance(host, { core: core, sounds: sounds, audio: audio });
      if (instance && instances) instances.set(host, instance);
      if (instance) {
        var pagehide = function (event) { if (event && event.persisted) suspendForBfcache(instance); else instance.cleanup(true); };
        var pageshow = function (event) { if (event && event.persisted) restoreFromBfcache(instance); };
        var visibility = function () { if (root.document.visibilityState === "hidden") stop(instance, false); };
        var sectionUnload = function (event) { if (event.target === host || (event.target && event.target.contains && event.target.contains(host))) instance.cleanup(true); };
        root.addEventListener("pagehide", pagehide);
        root.addEventListener("pageshow", pageshow);
        root.document.addEventListener("visibilitychange", visibility);
        root.document.addEventListener("shopify:section:unload", sectionUnload);
        instance.listeners.push([root, "pagehide", pagehide], [root, "pageshow", pageshow], [root.document, "visibilitychange", visibility], [root.document, "shopify:section:unload", sectionUnload]);
      }
    });
  }
  function teardown(scope) {
    if (!instances) return;
    Array.prototype.forEach.call(scope.querySelectorAll("[data-ac-metronome]"), function (host) { var instance = instances.get(host); if (instance) instance.cleanup(true); });
  }
  root.AudiocolourMetronome = Object.freeze({ init: init, teardown: teardown });
  if (root.document) {
    if (root.document.readyState === "loading") root.document.addEventListener("DOMContentLoaded", function () { init(root.document); });
    else init(root.document);
  }
}(window));
